import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Linkedin, Upload } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import Reveal from "@/components/reveal";

const FORM_NAME = "contact";
const MAX_CV_SIZE = 5 * 1024 * 1024;

const interestOptions = [
  { value: "employer-permanent", label: "Hiring — Permanent Recruitment" },
  { value: "employer-contract", label: "Hiring — Contract & Interim Talent" },
  { value: "employer-advisory", label: "Hiring — Talent Advisory" },
  { value: "cv", label: "Candidate — Share My CV" },
  { value: "connect", label: "Candidate — Career Conversation" },
  { value: "general", label: "General Enquiry" },
];

const inputCls =
  "w-full rounded-xl border border-[#E6E2D8] bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors duration-200 focus:border-[#FF5E1A]";

const Contact = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "general", message: "" });
  const [cvFile, setCvFile] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const interest = params.get("interest");
    if (interest && interestOptions.some((o) => o.value === interest)) {
      setForm((f) => ({ ...f, interest }));
    }
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const formElement = e.currentTarget;

    if (cvFile && cvFile.size > MAX_CV_SIZE) {
      toast.error("Your CV is too large.", { description: "Please choose a file smaller than 5 MB." });
      return;
    }

    setSending(true);
    try {
      const data = new FormData(formElement);
      let response;

      if (cvFile) {
        response = await fetch("/__forms.html", { method: "POST", body: data });
      } else {
        const encodedData = new URLSearchParams();
        data.forEach((value, key) => {
          if (typeof value === "string") encodedData.append(key, value);
        });

        response = await fetch("/__forms.html", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: encodedData.toString(),
        });
      }

      if (!response.ok) throw new Error("Request failed");
      toast.success("Thank you — your message has been received.", {
        description: "Saritha will get back to you personally, usually within one business day.",
      });
      formElement.reset();
      setForm({ name: "", email: "", phone: "", interest: form.interest, message: "" });
      setCvFile(null);
    } catch {
      toast.error("Something went wrong.", { description: "Please try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      {/* HERO (dark) */}
      <section className="bg-[#0B132B]">
        <div className="container-nb pt-32 pb-16 md:pt-40 md:pb-20">
          <Reveal>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.12] text-[#F8FAFC]">
              Let's find your next great hire.
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#94A3B8] leading-relaxed max-w-xl">
              Hiring technical talent, exploring your next role, or just want an honest read on the
              market — start here.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FORM + SIDEBAR (light) */}
      <section className="bg-[#FAF9F5]">
        <div className="container-nb py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 max-w-6xl mx-auto items-start">
            <Reveal className="lg:col-span-7">
              <form
                name={FORM_NAME}
                method="POST"
                encType="multipart/form-data"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={submit}
                className="card-light p-7 md:p-10 space-y-6"
                data-testid="contact-form"
              >
                <input type="hidden" name="form-name" value={FORM_NAME} />
                <input type="hidden" name="subject" value="New NicheBytes website enquiry" />
                <p className="hidden" aria-hidden="true">
                  <label>
                    Do not fill this out: <input name="bot-field" tabIndex={-1} autoComplete="off" />
                  </label>
                </p>
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-semibold text-[#0F172A] mb-2.5">Full Name*</label>
                  <input id="contact-name" name="name" autoComplete="name" data-testid="contact-input-name" required className={inputCls} value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-semibold text-[#0F172A] mb-2.5">Email*</label>
                  <input id="contact-email" name="email" autoComplete="email" data-testid="contact-input-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-semibold text-[#0F172A] mb-2.5">Phone Number*</label>
                  <input id="contact-phone" name="phone" autoComplete="tel" data-testid="contact-input-phone" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} />
                </div>
                <div>
                  <label htmlFor="contact-interest" className="block text-sm font-semibold text-[#0F172A] mb-2.5">I'm reaching out about</label>
                  <select id="contact-interest" name="interest" data-testid="contact-select-interest" className={inputCls} value={form.interest} onChange={set("interest")}>
                    <option value="general">Select one</option>
                    {interestOptions.filter((o) => o.value !== "general").map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-semibold text-[#0F172A] mb-2.5">Message</label>
                  <textarea id="contact-message" name="message" data-testid="contact-input-message" rows={5} className={inputCls} placeholder="Tell us about the role you're hiring for, or the role you're looking for." value={form.message} onChange={set("message")} />
                </div>
                {form.interest === "cv" && (
                  <div>
                    <label htmlFor="contact-cv" className="block text-sm font-semibold text-[#0F172A] mb-2.5">Attach your CV</label>
                    <label htmlFor="contact-cv" data-testid="contact-cv-upload" className="flex items-center gap-3 rounded-xl border border-dashed border-[#D8D2C4] bg-white px-4 py-4 text-sm text-[#475569] cursor-pointer transition-colors duration-200 hover:border-[#FF5E1A]">
                      <Upload size={16} className="text-[#FF5E1A] shrink-0" />
                      {cvFile ? cvFile.name : "Choose a file (PDF, DOC, max 5 MB) — it will be attached to your enquiry"}
                    </label>
                    <input id="contact-cv" name="cv" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setCvFile(e.target.files?.[0] || null)} />
                  </div>
                )}
                <button type="submit" data-testid="contact-form-submit-button" disabled={sending} className="btn-primary w-full justify-center rounded-lg disabled:opacity-60">
                  {sending ? "Sending…" : "Send message"}
                </button>
                <p className="text-xs text-[#94A3B8] text-center">
                  We only use your details to respond to your enquiry. See our{" "}
                  <Link to="/privacy" data-testid="contact-privacy-link" className="text-[#FF5E1A] underline underline-offset-2">Privacy Policy</Link>.
                </p>
              </form>
            </Reveal>
            <div className="lg:col-span-5 space-y-6">
              <Reveal delay={0.08}>
                <div className="card-light p-7 md:p-9" data-testid="what-happens-next">
                  <p className="flex items-center gap-2.5 font-mono-accent text-xs font-semibold tracking-[0.22em] uppercase text-[#FF5E1A] mb-2">
                    <span className="h-px w-5 bg-[#FF5E1A]" /> What Happens Next
                  </p>
                  {[
                    { num: "01", title: "Discovery call.", desc: "A 30-minute conversation about your role, team and technology environment." },
                    { num: "02", title: "Search strategy.", desc: "Within 48 hours: hiring approach, market insights and expected timeline." },
                    { num: "03", title: "Qualified shortlist.", desc: "Target 2–3 weeks after calibration: interview-ready, technically screened profiles." },
                  ].map((s) => (
                    <div key={s.num} className="flex gap-6 py-6 border-t border-[#E6E2D8] first:border-t-0">
                      <p className={`font-display text-4xl font-bold leading-none shrink-0 ${s.num === "03" ? "text-[#FF5E1A]" : "text-[#CBD5E1]"}`}>{s.num}</p>
                      <div>
                        <h3 className="font-display text-base font-semibold text-[#0F172A]">{s.title}</h3>
                        <p className="mt-1.5 text-sm text-[#64748B] leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.14}>
                <div className="card-light p-7 md:p-9" data-testid="direct-contact">
                  <p className="font-mono-accent text-xs font-semibold tracking-[0.22em] uppercase text-[#64748B] mb-6">Direct Contact</p>
                  <div className="flex items-center gap-4 mb-7">
                    <img
                      src="https://customer-assets-m6fa6gv7.emergentagent.net/job_boutique-recruit/artifacts/6jn2eo2o_ChatGPT%20Image%20Jul%2016%2C%202026%2C%2012_40_25%20PM%20%281%29.png"
                      alt="Saritha Jain"
                      className="w-12 h-12 rounded-full object-cover border border-[#E6E2D8]"
                    />
                    <div>
                      <p className="font-display text-base font-semibold text-[#0F172A]">Saritha Jain</p>
                      <p className="text-xs font-medium text-[#FF5E1A]">Founder · NicheBytes</p>
                    </div>
                  </div>
                  <div className="space-y-3.5 text-sm">
                    <p>
                      <span className="font-semibold text-[#0F172A]">Email</span><span className="text-[#94A3B8]"> · </span>
                      <a href="mailto:sarithajain@nichebytes.nl" data-testid="contact-email-link" className="text-[#475569] hover:text-[#FF5E1A] transition-colors duration-200">sarithajain@nichebytes.nl</a>
                    </p>
                    <p>
                      <span className="font-semibold text-[#0F172A]">Mobile</span><span className="text-[#94A3B8]"> · </span>
                      <a href="tel:+31684244330" className="text-[#475569] hover:text-[#FF5E1A] transition-colors duration-200">+31 684244330</a>
                    </p>
                    <p className="flex items-center gap-2.5">
                      <Linkedin size={15} className="text-[#FF5E1A] shrink-0" />
                      <a href="https://www.linkedin.com/in/sarithajain" target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-saritha" className="font-semibold text-[#0F172A] hover:text-[#FF5E1A] transition-colors duration-200">Saritha Jain</a>
                    </p>
                    <p className="flex items-center gap-2.5">
                      <Linkedin size={15} className="text-[#FF5E1A] shrink-0" />
                      <a href="https://www.linkedin.com/company/niche-bytes/?viewAsMember=true" target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-nichebytes" className="font-semibold text-[#0F172A] hover:text-[#FF5E1A] transition-colors duration-200">NicheBytes</a>
                    </p>
                    <p>
                      <span className="font-semibold text-[#0F172A]">Location</span><span className="text-[#94A3B8]"> · </span>
                      <span className="text-[#475569]">Utrecht, Netherlands</span>
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
